import React, { createContext, useContext, useState, ReactNode } from 'react';

interface FormContextValue {
  formData: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  resetForm: () => void;
}

const FormContext = createContext<FormContextValue | undefined>(undefined);

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within FormProvider');
  }
  return context;
};

interface FormProviderProps {
  children: ReactNode;
  initialData?: Record<string, any>;
  persistKey?: string;
}

export const FormProvider: React.FC<FormProviderProps> = ({
  children,
  initialData = {},
  persistKey,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    if (persistKey) {
      const saved = localStorage.getItem(persistKey);
      return saved ? JSON.parse(saved) : initialData;
    }
    return initialData;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(0);

  const resetForm = () => {
    setFormData(initialData);
    setErrors({});
    setCurrentStep(0);
    if (persistKey) {
      localStorage.removeItem(persistKey);
    }
  };

  const value: FormContextValue = {
    formData,
    setFormData,
    errors,
    setErrors,
    currentStep,
    setCurrentStep,
    resetForm,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
